<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  Skeleton,
  toast,
} from 'frappe-ui'

/** Local copy of FrappeUIError from frappe-ui composables. */
interface FrappeUIError extends Error {
  messages?: string[]
}
import { errorMessage, formatMoney } from '../api'
import { fetchCurrentTerms, requestTermsOtp, acceptTerms } from '../terms'
import type {
  CurrentTerms,
  TermsStatus,
  TermsOtpTiming,
  TermsAcceptResult,
} from '../types/api'

interface Props {
  /** When true, render as a Dialog instead of inline blocking card. */
  dialog?: boolean
  /** Terms status from boot (required for initial render). */
  status: TermsStatus
  /** Callback when terms are accepted (to refresh boot/unblock). */
  onAccepted?: () => void
  /** Callback when the dialog should close (dialog mode only). */
  onClose?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  dialog: false,
})

const status = ref<TermsStatus>(props.status)
const currentTerms = ref<CurrentTerms | null>(null)
const loadingTerms = ref(true)
const loadingTermsError = ref<string | null>(null)

const step = ref<string>('agree')
const agree = ref(false)
const typedName = ref('')
const otp = ref('')
const sendingOtp = ref(false)
const otpTiming = ref<TermsOtpTiming | null>(null)
const resendCooldown = ref(0)
let resendTimer: ReturnType<typeof setInterval> | null = null
const accepting = ref(false)
const inlineError = ref<string | undefined>(undefined)

watch(
  () => props.status,
  (newStatus) => {
    status.value = newStatus
    step.value = 'agree'
    agree.value = false
    typedName.value = ''
    otp.value = ''
    inlineError.value = undefined
    currentTerms.value = null
    loadingTerms.value = true
    loadCurrentTerms()
  },
)

function loadCurrentTerms() {
  loadingTerms.value = true
  loadingTermsError.value = null
  fetchCurrentTerms()
    .then((data) => {
      currentTerms.value = data
      status.value = data.status
    })
    .catch((e) => {
      loadingTermsError.value = errorMessage(e)
    })
    .finally(() => {
      loadingTerms.value = false
    })
}

onMounted(() => {
  loadCurrentTerms()
  if (resendCooldown.value > 0) startResendCountdown()
})

onUnmounted(() => {
  if (resendTimer) clearInterval(resendTimer)
})

function startResendCountdown() {
  if (resendTimer) clearInterval(resendTimer)
  resendTimer = setInterval(() => {
    if (resendCooldown.value > 0) {
      resendCooldown.value -= 1
    } else {
      if (resendTimer) clearInterval(resendTimer)
      resendTimer = null
    }
  }, 1000)
}

async function sendOtp() {
  inlineError.value = undefined
  if (!agree.value) {
    inlineError.value = 'You must agree to the terms to continue.'
    return
  }
  if (!typedName.value.trim() || typedName.value.trim().length < 2) {
    inlineError.value = 'Type your full name to sign (at least 2 characters).'
    return
  }
  if (typedName.value.trim().length > 140) {
    inlineError.value = 'Name must be 140 characters or fewer.'
    return
  }
  sendingOtp.value = true
  try {
    const timing = await requestTermsOtp()
    otpTiming.value = timing
    resendCooldown.value = timing.resend_after
    startResendCountdown()
    step.value = 'otp'
    otp.value = ''
  } catch (e) {
    inlineError.value = errorMessage(e)
    if (errorMessage(e).includes('already accepted')) {
      step.value = 'success'
      toast.success('The current terms are already accepted.')
      props.onAccepted?.()
    }
  } finally {
    sendingOtp.value = false
  }
}

async function resendOtp() {
  if (resendCooldown.value > 0) return
  inlineError.value = undefined
  sendingOtp.value = true
  try {
    const timing = await requestTermsOtp()
    otpTiming.value = timing
    resendCooldown.value = timing.resend_after
    startResendCountdown()
    otp.value = ''
  } catch (e) {
    inlineError.value = errorMessage(e)
  } finally {
    sendingOtp.value = false
  }
}

async function doAccept() {
  inlineError.value = undefined
  if (!otp.value.trim() || otp.value.trim().length !== 6) {
    inlineError.value = 'Enter the 6-digit code.'
    return
  }
  if (!currentTerms.value) {
    inlineError.value = 'Terms not loaded. Please retry.'
    return
  }
  accepting.value = true
  try {
    await acceptTerms({
      version: currentTerms.value.terms.version,
      otp: otp.value.trim(),
      typed_name: typedName.value.trim(),
      agree: true,
    })
    step.value = 'success'
    toast.success(
      'Terms accepted. The signed agreement PDF has been emailed to you.',
    )
    props.onAccepted?.()
    if (props.dialog) props.onClose?.()
  } catch (e) {
    const msg = errorMessage(e)
    inlineError.value = msg
    if (msg.includes('changed') || msg.includes('Reload')) {
      loadCurrentTerms()
    }
  } finally {
    accepting.value = false
  }
}

function refreshStatus() {
  loadCurrentTerms()
}

function backToAgree() {
  step.value = 'agree'
  inlineError.value = undefined
}

const isPlaceholder = computed(
  () => currentTerms.value?.terms.is_placeholder === 1,
)
const fee = computed(() => currentTerms.value?.fee)
const termsText = computed(() => currentTerms.value?.terms.text ?? '')
const termsTitle = computed(() => currentTerms.value?.terms.title ?? '')
const termsVersion = computed(
  () => currentTerms.value?.terms.version ?? status.value.version ?? '',
)
const requiresReacceptance = computed(
  () => status.value.requires_reacceptance === 1,
)
const canAccept = computed(() => status.value.can_accept)
const isRequired = computed(() => status.value.state === 'required')
const isGrace = computed(() => status.value.state === 'grace')
const acceptedAt = computed(() => status.value.accepted_at)
const acceptedByName = computed(() => status.value.accepted_by_name)
const acceptedVersion = computed(() => status.value.accepted_version)

const feeRate = computed(() => {
  const r = fee.value?.fee_rate
  return r != null ? (r % 1 === 0 ? String(r) : r.toFixed(2)) : '—'
})
const feeThreshold = computed(() =>
  fee.value != null
    ? formatMoney(fee.value.fee_threshold, fee.value.fee_currency)
    : '—',
)
const feeCap = computed(() =>
  fee.value != null
    ? formatMoney(fee.value.fee_cap, fee.value.fee_currency)
    : '—',
)
const feeCurrency = computed(() => fee.value?.fee_currency ?? 'KES')

const statusDescription = computed(() => {
  const s = status.value
  switch (s.state) {
    case 'accepted':
      return `Accepted version ${s.accepted_version} on ${s.accepted_at?.slice(0, 10) || '—'} by ${s.accepted_by_name || '—'}.`
    case 'disabled':
      return s.placeholder_unacceptable
        ? 'The platform fee terms are a draft placeholder and cannot be accepted. No salon is locked out.'
        : 'The platform fee terms are currently disabled.'
    case 'not_applicable':
      return 'Platform fee terms do not apply to this account.'
    default:
      return 'No action required.'
  }
})
</script>

<template>
  <div :class="dialog ? 'w-full max-w-2xl' : 'w-full max-w-2xl mx-auto'">
    <!-- Loading skeleton -->
    <div v-if="loadingTerms" class="space-y-4">
      <Skeleton class="h-8 w-3/4" />
      <Skeleton class="h-20 w-full" />
      <Skeleton class="h-32 w-full" />
      <Skeleton class="h-12 w-full" />
    </div>

    <!-- Error loading terms -->
    <Alert
      v-else-if="loadingTermsError"
      theme="red"
      title="Couldn't load the platform fee terms"
      :description="loadingTermsError"
      :dismissible="false"
    >
      <template #footer>
        <Button variant="subtle" label="Retry" @click="loadCurrentTerms" />
      </template>
    </Alert>

    <!-- Non-owner blocked -->
    <div
      v-else-if="!canAccept && (isRequired || isGrace)"
      class="rounded-lg border border-outline-gray-1 bg-surface-elevation-1 p-6 text-center"
    >
      <span
        class="lucide-alert-triangle size-10 text-ink-amber-3 mx-auto"
        aria-hidden="true"
      />
      <h2 class="mt-3 text-lg font-semibold text-ink-gray-9">
        Terms acceptance required
      </h2>
      <p class="mt-2 text-sm text-ink-gray-6">
        The salon owner must accept the platform fee terms before POS,
        appointments and online booking can be used.
      </p>
      <div class="mt-4 flex justify-center gap-2">
        <Button
          variant="outline"
          label="Refresh"
          icon-left="lucide-refresh-cw"
          @click="refreshStatus"
        />
      </div>
    </div>

    <!-- Owner acceptance flow -->
    <div v-else-if="canAccept && (isRequired || isGrace)">
      <!-- Placeholder warning -->
      <Alert
        v-if="isPlaceholder"
        theme="yellow"
        title="DRAFT PLACEHOLDER, NOT LEGALLY REVIEWED. NOT FINAL."
        description="These terms cannot be accepted yet. They have not been published for use."
        :dismissible="false"
        class="mb-4"
      />

      <!-- Terms title/version -->
      <div class="mb-4">
        <h3 class="text-base font-medium text-ink-gray-9">{{ termsTitle }}</h3>
        <p class="text-sm text-ink-gray-5">Version: {{ termsVersion }}</p>
        <p v-if="requiresReacceptance" class="mt-1 text-sm text-ink-amber-3">
          These terms have changed since you last accepted. Re-acceptance is
          required.
        </p>
      </div>

      <!-- Fee summary -->
      <div
        class="rounded-lg border border-outline-gray-1 bg-surface-gray-1 p-4 mb-4"
      >
        <p class="text-xs font-medium uppercase tracking-wide text-ink-gray-6">
          Platform fee summary
        </p>
        <dl class="mt-2 grid grid-cols-2 gap-2 text-sm">
          <dt class="text-ink-gray-5">Rate</dt>
          <dd class="font-medium text-ink-gray-9">{{ feeRate }}%</dd>
          <dt class="text-ink-gray-5">Threshold</dt>
          <dd class="font-medium text-ink-gray-9">
            {{ feeThreshold }} per invoice
          </dd>
          <dt class="text-ink-gray-5">Cap</dt>
          <dd class="font-medium text-ink-gray-9">{{ feeCap }} per invoice</dd>
          <dt class="text-ink-gray-5">Currency</dt>
          <dd class="font-medium text-ink-gray-9">{{ feeCurrency }}</dd>
        </dl>
      </div>

      <!-- Terms text -->
      <div
        class="rounded-lg border border-outline-gray-1 bg-surface-elevation-1 p-4 mb-4 max-h-60 overflow-y-auto whitespace-pre-line text-sm text-ink-gray-7"
      >
        {{ termsText }}
      </div>

      <!-- Step 1: Agree + typed name -->
      <form
        v-if="step === 'agree'"
        class="space-y-4"
        @submit.prevent="sendOtp"
      >
        <Checkbox
          v-model="agree"
          label="I have read and agree to the platform fee terms"
        />
        <FormControl
          v-model="typedName"
          label="Type your full name to sign"
          placeholder="Your full name"
          maxlength="140"
          autocomplete="name"
          :disabled="sendingOtp"
          :error="inlineError"
          description="2–140 characters"
        />
        <Button
          class="w-full"
          variant="solid"
          theme="blue"
          label="Send confirmation code"
          :loading="sendingOtp"
          :disabled="
            sendingOtp ||
            !agree ||
            !typedName.trim() ||
            typedName.trim().length < 2
          "
          type="submit"
        />
      </form>

      <!-- Step 2: OTP input -->
      <form
        v-else-if="step === 'otp'"
        class="space-y-4"
        @submit.prevent="doAccept"
      >
        <p class="text-sm text-ink-gray-6">
          Enter the 6-digit code sent to your email.
        </p>
        <FormControl
          v-model="otp"
          label="Confirmation code"
          placeholder="000000"
          maxlength="6"
          inputmode="numeric"
          autocomplete="one-time-code"
          :disabled="accepting"
          :error="inlineError"
          description="6 digits"
        />
        <div class="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            :label="
              resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : 'Resend code'
            "
            :disabled="resendCooldown > 0 || sendingOtp"
            :loading="sendingOtp"
            @click="resendOtp"
          />
          <Button variant="ghost" size="sm" label="Back" @click="backToAgree" />
        </div>
        <Button
          class="w-full mt-2"
          variant="solid"
          theme="blue"
          label="Accept and sign"
          :loading="accepting"
          :disabled="accepting || !otp.trim() || otp.trim().length !== 6"
          type="submit"
        />
      </form>

      <!-- Step 3: Success -->
      <div
        v-else-if="step === 'success'"
        class="rounded-lg border border-outline-gray-1 bg-surface-green-2 p-6 text-center"
      >
        <span
          class="lucide-circle-check size-10 text-ink-green-3 mx-auto"
          aria-hidden="true"
        />
        <h2 class="mt-3 text-lg font-semibold text-ink-green-3">
          Terms accepted
        </h2>
        <p class="mt-1 text-sm text-ink-green-3">
          Accepted version
          <span class="font-medium">{{ acceptedVersion || termsVersion }}</span>
          on
          <span class="font-medium">{{
            acceptedAt ? acceptedAt.slice(0, 16).replace('T', ' ') : '—'
          }}</span>
          by <span class="font-medium">{{ acceptedByName || typedName }}</span
          >.
        </p>
        <p class="mt-2 text-sm text-ink-green-3">
          The signed agreement PDF has been emailed to you.
        </p>
        <Button
          v-if="!dialog"
          class="mt-4"
          variant="subtle"
          label="Continue"
          @click="props.onAccepted?.()"
        />
      </div>

      <!-- Already accepted display -->
      <div
        v-else-if="status.state === 'accepted'"
        class="rounded-lg border border-outline-gray-1 bg-surface-green-2 p-6 text-center"
      >
        <span
          class="lucide-circle-check size-10 text-ink-green-3 mx-auto"
          aria-hidden="true"
        />
        <h2 class="mt-3 text-lg font-semibold text-ink-green-3">
          Terms already accepted
        </h2>
        <p class="mt-1 text-sm text-ink-green-3">
          Version
          <span class="font-medium">{{ acceptedVersion }}</span> accepted on
          <span class="font-medium">{{
            acceptedAt ? acceptedAt.slice(0, 16).replace('T', ' ') : '—'
          }}</span>
          by <span class="font-medium">{{ acceptedByName }}</span
          >.
        </p>
        <p class="mt-2 text-sm text-ink-green-3">
          The signed agreement PDF was emailed at the time of acceptance.
        </p>
      </div>
    </div>

    <!-- Disabled / not applicable / accepted (non-blocking) -->
    <div
      v-else
      class="rounded-lg border border-outline-gray-1 bg-surface-gray-1 p-6 text-center"
    >
      <span
        class="lucide-info size-10 text-ink-gray-4 mx-auto"
        aria-hidden="true"
      />
      <h3 class="mt-3 text-base font-medium text-ink-gray-9">
        Platform fee terms status
      </h3>
      <p class="mt-1 text-sm text-ink-gray-5">
        {{ statusDescription }}
      </p>
    </div>
  </div>
</template>
